"use client";

import { useId, useRef, useState } from "react";
import { Check, MoveRight } from "lucide-react";

import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { HoneypotField } from "@/components/marketing/honeypot-field";
import { HONEYPOT_FIELD, ELAPSED_FIELD } from "@/lib/form-fields";
import { cn } from "@/lib/utils";

type Status = "idle" | "loading" | "success" | "error";

interface LocalEnquiryFormProps {
  /** Human-readable location, e.g. "Gloucester, Gloucestershire". */
  location: string;
  /** Tracking tag for where the enquiry came from. */
  source?: string;
}

/**
 * Local-training enquiry form: the conversion point on the city/county pages.
 * Submits to /api/contact via fetch with inline success/error states (no full
 * page navigation), with the location baked into the subject so leads are
 * routed by area. All fields use the accessible Input component / labelled
 * controls.
 */
export function LocalEnquiryForm({ location, source = "local-training" }: LocalEnquiryFormProps) {
  const id = useId();
  const [status, setStatus] = useState<Status>("idle");
  const [message, setMessage] = useState("");
  const startedAt = useRef(Date.now());

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);

    const name = String(data.get("name") ?? "").trim();
    const email = String(data.get("email") ?? "").trim();
    const company = String(data.get("company") ?? "").trim();
    const phone = String(data.get("phone") ?? "").trim();
    const note = String(data.get("message") ?? "").trim();

    const composedMessage = [
      note,
      phone ? `Phone: ${phone}` : "",
      `Location: ${location}`,
    ]
      .filter(Boolean)
      .join("\n");

    setStatus("loading");
    setMessage("");

    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name,
          email,
          company: company || undefined,
          subject: `Local AI training enquiry: ${location} (${source})`,
          message: composedMessage,
          [HONEYPOT_FIELD]: String(data.get(HONEYPOT_FIELD) ?? ""),
          [ELAPSED_FIELD]: Date.now() - startedAt.current,
        }),
      });
      const body = (await res.json().catch(() => ({}))) as {
        message?: string;
        error?: string;
      };
      if (!res.ok) {
        throw new Error(body.error ?? "Something went wrong. Please try again.");
      }
      setStatus("success");
      setMessage(
        body.message ??
          "Thank you. We've got your enquiry and will be in touch within one working day.",
      );
      form.reset();
    } catch (err) {
      setStatus("error");
      setMessage(
        err instanceof Error ? err.message : "Something went wrong. Please try again.",
      );
    }
  }

  if (status === "success") {
    return (
      <div
        role="status"
        className="rounded-md border border-success/40 bg-success/10 p-6"
      >
        <p className="flex items-center gap-2 font-heading text-lg font-semibold text-dark">
          <Check className="size-5 shrink-0 text-success" aria-hidden="true" />
          Enquiry sent
        </p>
        <p className="mt-2 text-sm text-mid">{message}</p>
        <p className="mt-3 border-t border-success/20 pt-3 text-sm text-mid">
          While you wait, your team can make a start on the{" "}
          <a
            href="/courses"
            className="font-semibold text-primary underline underline-offset-2 hover:text-secondary"
          >
            free online course
          </a>
          . Eight short lessons, no card needed.
        </p>
      </div>
    );
  }

  const messageId = `${id}-message`;
  const errorId = `${id}-error`;

  return (
    <form
      onSubmit={handleSubmit}
      noValidate
      className="space-y-6 rounded-md border border-line bg-paper p-6 sm:p-8"
    >
      <div className="grid gap-6 sm:grid-cols-2">
        <Input label="Your name" name="name" placeholder="Jane Smith" required autoComplete="name" />
        <Input
          label="Organisation"
          name="company"
          placeholder="Acme Ltd"
          autoComplete="organization"
        />
      </div>

      <div className="grid gap-6 sm:grid-cols-2">
        <Input
          label="Work email"
          name="email"
          type="email"
          placeholder="jane@acme.co.uk"
          required
          autoComplete="email"
        />
        <Input
          label="Phone (optional)"
          name="phone"
          type="tel"
          placeholder="01453 000000"
          autoComplete="tel"
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <label htmlFor={messageId} className="text-sm font-semibold text-dark">
          What would you like help with?
        </label>
        <textarea
          id={messageId}
          name="message"
          rows={4}
          required
          minLength={10}
          placeholder="Team size, the tools you use, and what you'd like your team to get out of AI training…"
          className={cn(
            "min-h-[120px] w-full rounded-md border border-line bg-paper px-4 py-2.5",
            "text-base text-dark placeholder:text-mid/60 transition-colors duration-150 hover:border-mid/50",
            "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-secondary",
          )}
        />
      </div>

      {status === "error" && (
        <p
          id={errorId}
          role="alert"
          className="rounded-md border border-error/40 bg-error/10 px-4 py-3 text-sm font-medium text-error"
        >
          {message}
        </p>
      )}

      <Button
        type="submit"
        size="lg"
        className="w-full sm:w-auto"
        disabled={status === "loading"}
        aria-describedby={status === "error" ? errorId : undefined}
      >
        {status === "loading" ? "Sending…" : "Request a date"}
        {status !== "loading" && <MoveRight className="size-5" aria-hidden="true" />}
      </Button>

      <p className="text-xs text-mid">
        We'll reply within one working day. No spam, ever.
      </p>

      <HoneypotField />
    </form>
  );
}
